// src/hooks/useProactiveSuggestion.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase';
import { usePriceVisibility } from './usePriceVisibility';
import { fieldReadTablesForRole } from '../utils/job/fieldReadTables';

function followUpNoteText(pending) {
  if (!pending) return '';
  if (typeof pending === 'object' && pending !== null && 'note' in pending) {
    return String(pending.note ?? '').trim();
  }
  return String(pending);
}

/**
 * useProactiveSuggestion — generates context-aware suggestions without an LLM call.
 *
 * @param {import('react-intl').IntlShape} params.intl — for localized greeting / chip copy
 */
export default function useProactiveSuggestion({
  role,
  customerId,
  currentPage,
  routeStops,
  followUpCount,
  /** Worker SPA: route vs dashboard tab (URL stays /dashboard) — from AiAssistantWorkerSurfaceContext */
  workerSurface,
  intl,
}) {
  const [suggestion, setSuggestion] = useState(null);
  const lastCustomerRef = useRef(null);

  // RD-ROLE-005b: a blind field role (worker/supervisor with canViewPrices()
  // === false) has no base `jobs`/`customers`/`job_line_items` SELECT — ANY
  // query against those tables returns 0 rows under RLS regardless of which
  // columns are selected, so this AI-suggestion fetch must route too or it
  // silently stops producing suggestions for a blind session (fetch resolves
  // empty, not an error — the catch-all below never fires).
  const { canSeePrices } = usePriceVisibility();
  const tables = fieldReadTablesForRole(role, canSeePrices);
  const blind = tables.jobs === 'field_jobs';

  // WorkerHome publishes a fresh array from .map() every render; the reference always
  // changes even when completion flags are unchanged. Depending on `routeStops` in the
  // effect below caused setSuggestion → re-render → new reference → infinite loop and
  // blocked React Router from committing navigations (e.g. /log-job/:id).
  const routeStopsSig =
    Array.isArray(routeStops) && routeStops.length
      ? routeStops.map((s) => (s.completed ? '1' : '0')).join('|')
      : '';

  const fetchCustomerContext = useCallback(async (custId) => {
    if (!custId || lastCustomerRef.current === custId) return;
    lastCustomerRef.current = custId;

    try {
      const { data: cust } = await supabase
        .from(tables.customers)
        .select('full_name, pending_follow_up')
        .eq('id', custId)
        .single();

      if (!cust) {
        setSuggestion(null);
        return;
      }

      const { data: jobs } = await supabase
        .from(tables.jobs)
        .select(blind ? 'id, date' : 'id, date, total')
        .eq('customer_id', custId)
        .order('date', { ascending: false })
        .limit(1);

      const lastJob = jobs?.[0];
      let lastServiceItems = [];
      if (lastJob) {
        const { data: items } = await supabase
          .from(tables.jobLineItems)
          .select('type, name, quantity')
          .eq('job_id', lastJob.id);
        lastServiceItems = items || [];
      }

      const parts = lastServiceItems
        .filter(i => i.type === 'service' || i.type === 'chemical')
        .map(i => i.name);
      const pattern = parts.join(' + ') || null;

      let daysSince = null;
      if (lastJob) {
        const diff = Date.now() - new Date(lastJob.date).getTime();
        daysSince = Math.max(0, Math.floor(diff / 86400000));
      }

      const fu = followUpNoteText(cust.pending_follow_up);
      const meta = [
        daysSince !== null
          ? intl.formatMessage(
            { id: 'aiAssistant.contextGreeting.lastServiced', defaultMessage: 'Last serviced {days} days ago' },
            { days: daysSince },
          )
          : null,
        fu
          ? intl.formatMessage(
            { id: 'aiAssistant.proactive.pendingFollowUpMeta', defaultMessage: 'Follow-up: {note}' },
            { note: fu },
          )
          : null,
      ].filter(Boolean).join(' \u00B7 ');

      const chips = [];
      if (pattern) {
        chips.push({
          label: intl.formatMessage(
            { id: 'aiAssistant.contextGreeting.likeLastTime', defaultMessage: '{pattern} like last time' },
            { pattern },
          ),
          action: intl.formatMessage(
            { id: 'aiAssistant.proactive.action.logPatternFor', defaultMessage: 'Log {pattern} for {name}' },
            { pattern, name: cust.full_name },
          ),
        });
      }
      chips.push({
        label: intl.formatMessage({ id: 'aiAssistant.contextGreeting.somethingDifferent', defaultMessage: 'Something different today' }),
        action: intl.formatMessage(
          { id: 'aiAssistant.proactive.action.logJobForCustomer', defaultMessage: 'Log a job for {name}' },
          { name: cust.full_name },
        ),
      });

      setSuggestion({
        suggestion: intl.formatMessage(
          { id: 'aiAssistant.contextGreeting.readyFor', defaultMessage: 'Ready for {name}' },
          { name: cust.full_name },
        ),
        meta: meta || null,
        chips,
        hasSuggestion: true,
        hasUrgentSuggestion: true,
      });
    } catch {
      setSuggestion(null);
    }
  }, [intl, tables, blind]);

  useEffect(() => {
    // workerSurface is role-agnostic despite the name — worker and
    // supervisor home screens both publish into it. Surface-aware
    // suggestions (route / dashboard) always beat URL-derived guesses.
    const surfaceRoleAware = (role === 'worker' || role === 'supervisor')
      ? workerSurface
      : null;
    const pageForProactive =
      currentPage === 'log_job' || currentPage === 'customer_detail'
        ? currentPage
        : surfaceRoleAware === 'route'
          ? 'route'
          : surfaceRoleAware === 'dashboard'
            ? 'dashboard'
            : currentPage;

    if ((pageForProactive === 'customer_detail' || pageForProactive === 'log_job') && customerId) {
      fetchCustomerContext(customerId);
      return;
    }

    if (
      pageForProactive === 'route'
      && (role === 'worker' || role === 'supervisor')
      && routeStops?.length
    ) {
      const remaining = routeStops.filter((s) => !s.completed).length;
      if (remaining > 0) {
        // Supervisors see team-wide data; keep the morning quiet so the
        // amber FAB dot isn't a constant distraction as they hop between
        // tabs. The greeting still renders if they open the panel — only
        // the pulse is gated. Workers keep the original always-on pulse
        // since their counts represent personal work they're expected
        // to clear today.
        const isSupervisor = role === 'supervisor';
        const hourOfDay = new Date().getHours();
        const hasUrgentSuggestion = isSupervisor ? hourOfDay >= 12 : true;

        const greetingId = isSupervisor
          ? 'aiAssistant.contextGreeting.teamStopsRemaining'
          : 'aiAssistant.contextGreeting.stopsRemaining';
        const greetingFallback = isSupervisor
          ? 'Your team has {count} stops pending today'
          : 'You have {count} stops remaining today';

        const chipQueryId = isSupervisor
          ? 'aiAssistant.proactive.query.teamStopsLeft'
          : 'aiAssistant.proactive.query.stopsLeft';
        const chipQueryFallback = isSupervisor
          ? 'What stops does the team have today?'
          : 'What stops do I have left today?';

        setSuggestion({
          suggestion: intl.formatMessage(
            { id: greetingId, defaultMessage: greetingFallback },
            { count: remaining },
          ),
          meta: null,
          chips: [{
            label: intl.formatMessage({ id: 'aiAssistant.proactive.chip.startLogging', defaultMessage: 'Start logging' }),
            action: intl.formatMessage({
              id: chipQueryId,
              defaultMessage: chipQueryFallback,
            }),
          }],
          hasSuggestion: true,
          hasUrgentSuggestion,
        });
      } else {
        setSuggestion(null);
      }
      return;
    }

    if (pageForProactive === 'dashboard' && typeof followUpCount === 'number' && followUpCount > 0) {
      setSuggestion({
        suggestion: intl.formatMessage(
          { id: 'aiAssistant.contextGreeting.followUps', defaultMessage: '{count} follow-ups need attention' },
          { count: followUpCount },
        ),
        meta: null,
        chips: [{
          label: intl.formatMessage({ id: 'aiAssistant.proactive.chip.showFollowUps', defaultMessage: 'Show follow-ups' }),
          action: intl.formatMessage({
            id: 'aiAssistant.proactive.query.showFollowUps',
            defaultMessage: 'Which customers need follow-up?',
          }),
        }],
        hasSuggestion: true,
        hasUrgentSuggestion: true,
      });
      return;
    }

    lastCustomerRef.current = null;
    setSuggestion(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- routeStops identity changes every render; routeStopsSig is the stable content key.
  }, [currentPage, customerId, role, routeStopsSig, followUpCount, workerSurface, fetchCustomerContext, intl]);

  const clearSuggestion = useCallback(() => setSuggestion(null), []);

  return {
    suggestion,
    hasSuggestion: suggestion?.hasSuggestion || false,
    // Drives the FAB's amber pulse. Split from `hasSuggestion` so the
    // in-panel greeting can still show when the panel is explicitly
    // opened, without forcing a persistent dot in quieter contexts
    // (e.g. supervisors in the morning).
    hasUrgentSuggestion: suggestion?.hasUrgentSuggestion || false,
    clearSuggestion,
  };
}
